import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu-12';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Tooltip from '@atlaskit/tooltip';

import { useIntl } from 'src/hooks/intl';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { onFilterModeChange } from '../actions/filter-files';
import { FilterMode } from '../constants';
import { getFilterMode } from '../reducers/section';

import messages from './filter-mode-selector.i18n';
import * as styles from './filter-mode-selector.style';

type FilterModeSelectorProps = {
  onChange: () => void;
  isMainBranch: boolean;
};

export const FilterModeSelector: React.FC<FilterModeSelectorProps> = ({
  onChange,
  isMainBranch,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const filterMode = useSelector(getFilterMode);
  const [isSelected, setIsSelected] = useState(false);

  // Call the "onChange" handler when the filterMode is changed.
  useEffect(() => {
    onChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode]);

  const options = [
    {
      label: intl.formatMessage(messages.filesOption),
      value: FilterMode.Files,
    },
    {
      label: intl.formatMessage(messages.codeOption),
      value: FilterMode.Code,
    },
  ];
  const selectedOption = options.find(({ value }) => value === filterMode);

  return (
    <>
      <styles.FilterModeSelectorContainer>
        <DropdownMenu
          onOpenChange={() => setIsSelected(!isSelected)}
          trigger={({ triggerRef, ...props }) => (
            <styles.FilterModeSelectorTriggerContainer>
              <Tooltip
                position="top"
                content={intl.formatMessage(messages.tooltipLabel)}
              >
                <Button
                  {...props}
                  ref={triggerRef}
                  iconAfter={<ChevronDownIcon label="" />}
                  isSelected={isSelected}
                  appearance="subtle"
                  shouldFitContainer
                >
                  <span>{selectedOption?.label}</span>
                </Button>
              </Tooltip>
            </styles.FilterModeSelectorTriggerContainer>
          )}
        >
          <DropdownItemGroup>
            {options.map(({ label, value }) => (
              <Tooltip
                key={value}
                content={
                  !isMainBranch && value === FilterMode.Code
                    ? intl.formatMessage(messages.codeSearchDisabled)
                    : undefined
                }
              >
                <styles.FilterOptionWrapper
                  onClick={() => {
                    publishUiEvent({
                      action: 'changed',
                      actionSubject: 'option',
                      actionSubjectId: 'filterMode',
                      source: 'repositorySourceScreen',
                      attributes: {
                        value,
                      },
                    });
                    dispatch(onFilterModeChange(value));
                  }}
                  isSelected={selectedOption?.label === label}
                  isDisabled={!isMainBranch && value === FilterMode.Code}
                >
                  {label}
                </styles.FilterOptionWrapper>
              </Tooltip>
            ))}
          </DropdownItemGroup>
        </DropdownMenu>
      </styles.FilterModeSelectorContainer>
      <styles.FilterModeSelectorSeparator />
    </>
  );
};
